import React from 'react';
import type { WrappedComponentProps } from 'react-intl';

import Spinner from '@atlaskit/spinner/spinner';
import type { DocNode } from '@atlaskit/adf-schema/doc';
import type { EditorActions } from '@atlaskit/editor-core/actions';

import type { LoadableType } from '@confluence/loadable';
import { LoadableLazy } from '@confluence/loadable';
import type { CreateInlineCommentHighlight } from '@confluence/comment-context/entry-points/InlineCommentsContext.ts';
import type { CommentActionType } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import { LazyCommentEditorPlaceholder } from './LazyCommentEditorPlaceholder.tsx';
import type { OnContentChangeWithDataHandler } from './CommentEditor.tsx';
import type {
	EditorAppearance,
	EditorCommentMode,
	EditorCommentType,
	AfterEditorActivateCallback,
	InlineCommentsMode,
} from './commentTypes.ts';

type ChromelessEditorComponent =
	(typeof import('@atlaskit/editor-core/appearance-editor-chromeless'))['ChromelessEditor'];
type CommentEditorComponent =
	(typeof import('@atlaskit/editor-core/appearance-editor-comment'))['CommentEditor'];

export const LazyCommentEditor: LoadableType<
	Omit<
		Omit<
			WrappedComponentProps & {
				newCommentHighlight?: CreateInlineCommentHighlight;
				pageId: string;
				pageType: string;
				appearance: EditorAppearance;
				commentId?: string;
				commentMode: EditorCommentMode;
				commentType: EditorCommentType;
				spaceId: string;
				onSaveComment?: (adf: any, onSuccess: () => void) => Promise<any>;
				content?: DocNode;
				useNewWarningModal?: boolean;
				showCancelButton?: boolean;
				onCancelComment?: (params?: { destroy?: boolean }) => void;
				onContentChange?: (hasContentChange: boolean) => void;
				onContentChangeWithData?: OnContentChangeWithDataHandler;
				onEditorReady?: (editorActions: EditorActions) => void;
				afterActivation?: AfterEditorActivateCallback;
				hasMediaUploadPermissions: boolean;
				shouldResetEditor?: boolean;
				onEditorReset?: () => void;
				expandEditor?: boolean;
				shouldWarnOnInternalNavigation?: boolean;
				hideWatchCheckbox?: boolean;
				pageMode?: InlineCommentsMode;
				flags: FlagsStateContainer;
				setShouldRefetch?: () => void;
				EditorComponent: CommentEditorComponent | ChromelessEditorComponent;
				commentContainerElementRef?: React.MutableRefObject<HTMLElement | null>;
				isCommentsPanel?: boolean;
				isFooterComment?: boolean;
				wasRemovedByAnotherUser?: CommentActionType | boolean;
				wasDraftAnnotationRemoved?: boolean;
				isStandaloneComment?: boolean;
				isOffline?: boolean;
				customAriaLabel?: string;
				popupsMountPoint?: HTMLElement;
				popupsScrollableElement?: HTMLElement;
				hasParentMaxHeight?: boolean;
			} & {
				topLevelCommentId?: string;
			},
			'intl'
		> & {
			forwardedRef?: React.Ref<any>;
		},
		'flags'
	>
> = LoadableLazy({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-CommentEditor" */ './CommentEditor.tsx'))
			.CommentEditor,
	loading: () => (
		<div>
			<Spinner size="medium" interactionName="comment-editor-loadable" />
		</div>
	),
	waitForNetworkOnline: true,
});

export const LazyInlineCommentEditor: LoadableType<
	Omit<
		Omit<
			WrappedComponentProps & {
				newCommentHighlight?: CreateInlineCommentHighlight;
				pageId: string;
				pageType: string;
				appearance: EditorAppearance;
				commentId?: string;
				commentMode: EditorCommentMode;
				commentType: EditorCommentType;
				spaceId: string;
				onSaveComment?: (adf: any, onSuccess: () => void) => Promise<any>;
				content?: DocNode;
				useNewWarningModal?: boolean;
				showCancelButton?: boolean;
				onCancelComment?: (params?: { destroy?: boolean }) => void;
				onContentChange?: (hasContentChange: boolean) => void;
				onContentChangeWithData?: OnContentChangeWithDataHandler;
				onEditorReady?: (editorActions: EditorActions) => void;
				afterActivation?: AfterEditorActivateCallback;
				hasMediaUploadPermissions: boolean;
				shouldResetEditor?: boolean;
				onEditorReset?: () => void;
				expandEditor?: boolean;
				shouldWarnOnInternalNavigation?: boolean;
				hideWatchCheckbox?: boolean;
				pageMode?: InlineCommentsMode;
				flags: FlagsStateContainer;
				setShouldRefetch?: () => void;
				EditorComponent: CommentEditorComponent | ChromelessEditorComponent;
				commentContainerElementRef?: React.MutableRefObject<HTMLElement | null>;
				isCommentsPanel?: boolean;
				isFooterComment?: boolean;
				wasRemovedByAnotherUser?: CommentActionType | boolean;
				wasDraftAnnotationRemoved?: boolean;
				isStandaloneComment?: boolean;
				isOffline?: boolean;
				customAriaLabel?: string;
				popupsMountPoint?: HTMLElement;
				popupsScrollableElement?: HTMLElement;
				hasParentMaxHeight?: boolean;
			} & {
				topLevelCommentId?: string;
			},
			'intl'
		> & {
			forwardedRef?: React.Ref<any>;
		},
		'flags'
	>
> = LoadableLazy({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-CommentEditor" */ './CommentEditor.tsx'))
			.CommentEditor,
	loading: () => <LazyCommentEditorPlaceholder />,
	waitForNetworkOnline: true,
});

export const LazyCommentEditorWithoutSpinner: LoadableType<
	Omit<
		Omit<
			WrappedComponentProps & {
				newCommentHighlight?: CreateInlineCommentHighlight;
				pageId: string;
				pageType: string;
				appearance: EditorAppearance;
				commentId?: string;
				commentMode: EditorCommentMode;
				commentType: EditorCommentType;
				spaceId: string;
				onSaveComment?: (adf: any, onSuccess: () => void) => Promise<any>;
				content?: DocNode;
				useNewWarningModal?: boolean;
				showCancelButton?: boolean;
				onCancelComment?: (params?: { destroy?: boolean }) => void;
				onContentChange?: (hasContentChange: boolean) => void;
				onContentChangeWithData?: OnContentChangeWithDataHandler;
				onEditorReady?: (editorActions: EditorActions) => void;
				afterActivation?: AfterEditorActivateCallback;
				hasMediaUploadPermissions: boolean;
				shouldResetEditor?: boolean;
				onEditorReset?: () => void;
				expandEditor?: boolean;
				shouldWarnOnInternalNavigation?: boolean;
				hideWatchCheckbox?: boolean;
				pageMode?: InlineCommentsMode;
				flags: FlagsStateContainer;
				setShouldRefetch?: () => void;
				EditorComponent: CommentEditorComponent | ChromelessEditorComponent;
				commentContainerElementRef?: React.MutableRefObject<HTMLElement | null>;
				isCommentsPanel?: boolean;
				isFooterComment?: boolean;
				wasRemovedByAnotherUser?: CommentActionType | boolean;
				wasDraftAnnotationRemoved?: boolean;
				isStandaloneComment?: boolean;
				isOffline?: boolean;
				customAriaLabel?: string;
				popupsMountPoint?: HTMLElement;
				popupsScrollableElement?: HTMLElement;
				hasParentMaxHeight?: boolean;
			} & {
				topLevelCommentId?: string;
			},
			'intl'
		> & {
			forwardedRef?: React.Ref<any>;
		},
		'flags'
	>
> = LoadableLazy({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-CommentEditor" */ './CommentEditor.tsx'))
			.CommentEditor,
	waitForNetworkOnline: true,
});
